import React, { useRef, useCallback, useState } from 'react';
import {
  X,
  Plus,
  Check,
  ChartLine,
  ChartBar,
  Table,
  Funnel,
  Gauge,
  ChartPie,
  Users,
  GenderIntersex,
  Calendar,
  Briefcase,
  ListBullets,
  MapPin,
  UserCircle,
  Buildings,
  Clock,
  TrendUp,
  Cube,
  Target,
  Star,
  Lightning,
  ChartDonut,
  Medal,
  CheckCircle,
  Ranking,
  ClockCounterClockwise,
  ArrowsClockwise,
  ChatCircleDots,
  ChatsCircle,
  Warning,
  BookOpen,
  Clipboard,
  ChartLineUp,
  Percent,
  UserPlus,
  Hourglass,
  GraduationCap,
  Certificate,
  Path,
  ShoppingCart,
  Handshake,
  TreeStructure,
} from '@phosphor-icons/react';
import type { WidgetTemplate } from '../../types/widget';
import { useSubscriptionStore } from '../../stores/subscriptionStore';
import { useAuth } from '../../context/AuthContextBackend';

interface WidgetTemplateLibraryProps {
  isOpen: boolean;
  onClose: () => void;
  templates: WidgetTemplate[];
  onAddTemplate: (template: WidgetTemplate) => void;
  onAddTemplates?: (templates: WidgetTemplate[]) => void;
  loading?: boolean;
}

const getTemplateIcon = (template: WidgetTemplate, size: number = 32) => {
  // Strip scoping suffix to match base template key
  const baseKey = (template.key || '')
    .replace(/_(?:global_hr|executive|manager|scoped_hr|self)$/, '');

  switch (baseKey) {
    // Workforce widgets
    case 'wf_active_employees':
      return <Users size={size} weight="duotone" />;
    case 'wf_employees_by_gender':
      return <GenderIntersex size={size} weight="duotone" />;
    case 'wf_employees_by_age':
      return <Calendar size={size} weight="duotone" />;
    case 'wf_employees_by_type':
      return <Briefcase size={size} weight="duotone" />;
    case 'wf_employee_records':
      return <ListBullets size={size} weight="duotone" />;
    case 'wf_employees_by_location':
      return <MapPin size={size} weight="duotone" />;
    case 'wf_employees_by_department':
      return <Buildings size={size} weight="duotone" />;
    case 'wf_employees_by_tenure':
      return <Clock size={size} weight="duotone" />;
    case 'wf_turnover_rate':
    case 'wf_attrition_rate':
      return <TrendUp size={size} weight="duotone" />;
    case 'wf_new_employees':
    case 'wf_new_hires':
      return <UserPlus size={size} weight="duotone" />;
    case 'wf_employees_by_job_title':
      return <Briefcase size={size} weight="duotone" />;
    case 'wf_headcount_trend':
      return <ChartLineUp size={size} weight="duotone" />;
    case 'wf_tenure_distribution':
      return <Hourglass size={size} weight="duotone" />;

    // My Portal widgets
    case 'mp_user_card':
      return <UserCircle size={size} weight="duotone" />;
    case 'mp_review_summary':
      return <Star size={size} weight="duotone" />;
    case 'mp_goal_summary':
      return <ChartDonut size={size} weight="duotone" />;
    case 'mp_goals_list':
      return <Target size={size} weight="duotone" />;
    case 'mp_participation':
      return <Lightning size={size} weight="duotone" />;
    case 'mp_top_competencies':
      return <Medal size={size} weight="duotone" />;
    case 'mp_improvement_competencies':
      return <Ranking size={size} weight="duotone" />;
    case 'mp_actions':
      return <CheckCircle size={size} weight="duotone" />;
    case 'mp_rating_by_review':
      return <ChartBar size={size} weight="duotone" />;
    case 'mp_rating_by_competency':
      return <Star size={size} weight="duotone" />;

    // New My Portal widgets
    case 'mp_review_cycles_history':
      return <ClockCounterClockwise size={size} weight="duotone" />;
    case 'mp_checkin_summary':
      return <ArrowsClockwise size={size} weight="duotone" />;
    case 'mp_360_feedback_as_reviewer':
      return <ChatCircleDots size={size} weight="duotone" />;
    case 'mp_360_feedback_as_reviewee':
      return <ChatsCircle size={size} weight="duotone" />;

    // Cycles widgets
    case 'cycles_overview_summary':
      return <Cube size={size} weight="duotone" />;
    case 'cycles_active_count':
      return <Lightning size={size} weight="duotone" />;
    case 'cycles_completion_average':
      return <Gauge size={size} weight="duotone" />;
    case 'cycles_at_risk_count':
      return <Warning size={size} weight="duotone" />;
    case 'cycles_by_type':
      return <ChartPie size={size} weight="duotone" />;
    case 'cycles_by_completion':
      return <ChartBar size={size} weight="duotone" />;
    case 'cycles_list':
      return <ListBullets size={size} weight="duotone" />;
    case 'cycles_at_risk_list':
      return <ListBullets size={size} weight="duotone" />;

    // Goalboard widgets
    case 'goalboard_employee_summary':
      return <Users size={size} weight="duotone" />;
    case 'goalboard_goal_status_summary':
      return <ChartDonut size={size} weight="duotone" />;
    case 'goalboard_goals_list':
      return <Target size={size} weight="duotone" />;
    case 'goalboard_employees_without_goals':
      return <UserCircle size={size} weight="duotone" />;
    case 'goalboard_stale_goals':
      return <Clock size={size} weight="duotone" />;

    // IDP widgets
    case 'idp_overview_summary':
      return <BookOpen size={size} weight="duotone" />;
    case 'idp_by_status':
      return <ChartDonut size={size} weight="duotone" />;
    case 'idp_list':
      return <BookOpen size={size} weight="duotone" />;
    case 'idp_completion_rate':
      return <Gauge size={size} weight="duotone" />;
    case 'idp_employees_without_idps':
      return <UserCircle size={size} weight="duotone" />;

    // PIP widgets
    case 'pip_overview_summary':
      return <Warning size={size} weight="duotone" />;
    case 'pip_by_status':
      return <ChartDonut size={size} weight="duotone" />;
    case 'pip_list':
      return <Clipboard size={size} weight="duotone" />;
    case 'pip_at_risk_list':
      return <Warning size={size} weight="duotone" />;
    case 'pip_success_rate':
      return <Percent size={size} weight="duotone" />;

    // Survey widgets
    case 'survey_overview_summary':
      return <ChatCircleDots size={size} weight="duotone" />;
    case 'survey_response_rate':
      return <Gauge size={size} weight="duotone" />;
    case 'survey_by_status':
      return <ChartDonut size={size} weight="duotone" />;
    case 'survey_list':
      return <Clipboard size={size} weight="duotone" />;
    case 'survey_participation_by_department':
      return <ChartBar size={size} weight="duotone" />;

    // Card view widget types
    case 'cycle_card':
      return <ArrowsClockwise size={size} weight="duotone" />;
    case 'at_risk_card':
      return <Warning size={size} weight="duotone" />;
    case 'goal_card':
      return <Target size={size} weight="duotone" />;
    case 'employee_card':
      return <UserCircle size={size} weight="duotone" />;
    case 'stale_goal_card':
      return <ClockCounterClockwise size={size} weight="duotone" />;
  }

  // Learn module widgets — match by prefix
  if (baseKey.startsWith('learn_overview_') || baseKey.startsWith('learn_enrollment_'))
    return <GraduationCap size={size} weight="duotone" />;
  if (baseKey.startsWith('learn_completion_') || baseKey.startsWith('learn_avg_'))
    return <ChartBar size={size} weight="duotone" />;
  if (baseKey.startsWith('learn_overdue_'))
    return <Warning size={size} weight="duotone" />;
  if (baseKey.startsWith('learn_course_'))
    return <BookOpen size={size} weight="duotone" />;
  if (baseKey.startsWith('learn_license_'))
    return <Gauge size={size} weight="duotone" />;
  if (baseKey.startsWith('learn_lp_'))
    return <Path size={size} weight="duotone" />;
  if (baseKey.startsWith('learn_assignment_'))
    return <ChartDonut size={size} weight="duotone" />;
  if (baseKey.startsWith('learn_cust_'))
    return <Buildings size={size} weight="duotone" />;
  if (baseKey.startsWith('learn_marketplace_'))
    return <ShoppingCart size={size} weight="duotone" />;
  if (baseKey.startsWith('learn_trainer_') || baseKey.startsWith('learn_ext_'))
    return <Briefcase size={size} weight="duotone" />;
  if (baseKey.startsWith('learn_ilt_'))
    return <Calendar size={size} weight="duotone" />;
  if (baseKey.startsWith('learn_mgr_'))
    return <Users size={size} weight="duotone" />;
  if (baseKey.startsWith('learn_coach_'))
    return <Handshake size={size} weight="duotone" />;
  if (baseKey.startsWith('learn_uh_'))
    return <TreeStructure size={size} weight="duotone" />;
  if (baseKey.startsWith('mp_learn_'))
    return <GraduationCap size={size} weight="duotone" />;

  // Fallback to chart type based icons
  const chartType = (template.default_config as any)?.chart_type as string;

  switch (chartType) {
    case 'line':
      return <ChartLine size={size} weight="duotone" />;
    case 'bar':
      return <ChartBar size={size} weight="duotone" />;
    case 'funnel':
      return <Funnel size={size} weight="duotone" />;
    case 'gauge':
      return <Gauge size={size} weight="duotone" />;
    case 'pie':
      return <ChartPie size={size} weight="duotone" />;
    case 'donut':
      return <ChartDonut size={size} weight="duotone" />;
    case 'kpi_card':
      return <Lightning size={size} weight="duotone" />;
    case 'multi_stat':
      return <Cube size={size} weight="duotone" />;
    case 'map':
      return <MapPin size={size} weight="duotone" />;
    case 'pip_card':
      return <Warning size={size} weight="duotone" />;
    case 'idp_card':
      return <BookOpen size={size} weight="duotone" />;
    case 'survey_card':
      return <ChatCircleDots size={size} weight="duotone" />;
    case 'cycle_card':
      return <ArrowsClockwise size={size} weight="duotone" />;
    case 'at_risk_card':
      return <Warning size={size} weight="duotone" />;
    case 'goal_card':
      return <Target size={size} weight="duotone" />;
    case 'employee_card':
      return <UserCircle size={size} weight="duotone" />;
    case 'stale_goal_card':
      return <ClockCounterClockwise size={size} weight="duotone" />;
    default:
      if ((template.default_config as any)?.display_type === 'card') {
        return <Cube size={size} weight="duotone" />;
      }
      if ((template.default_config as any)?.page_size) {
        return <Table size={size} weight="duotone" />;
      }
      return <ChartBar size={size} weight="duotone" />;
  }
};

const getTemplateColor = (template: WidgetTemplate): string => {
  const widgetColors: Record<string, string> = {
    // Workforce widgets
    wf_active_employees: '#3b82f6',
    wf_employees_by_gender: '#ec4899',
    wf_employees_by_age: '#f59e0b',
    wf_employees_by_type: '#8b5cf6',
    wf_employee_records: '#14b8a6',
    wf_employees_by_location: '#10b981',
    wf_employees_by_department: '#6366f1',
    wf_employees_by_tenure: '#0ea5e9',
    wf_turnover_rate: '#ef4444',
    wf_attrition_rate: '#ef4444',
    wf_new_employees: '#22c55e',
    wf_new_hires: '#22c55e',
    wf_employees_by_job_title: '#8b5cf6',
    wf_headcount_trend: '#3b82f6',
    wf_tenure_distribution: '#0ea5e9',

    // My Portal widgets
    mp_user_card: '#6366f1',
    mp_review_summary: '#f59e0b',
    mp_goal_summary: '#10b981',
    mp_goals_list: '#14b8a6',
    mp_participation: '#8b5cf6',
    mp_top_competencies: '#22c55e',
    mp_improvement_competencies: '#f97316',
    mp_actions: '#0ea5e9',
    mp_rating_by_review: '#3b82f6',
    mp_rating_by_competency: '#ec4899',

    // New My Portal widgets - vibrant colors
    mp_review_cycles_history: '#7c3aed', // Violet - history/timeline
    mp_checkin_summary: '#06b6d4',       // Cyan - recurring check-ins
    mp_360_feedback_as_reviewer: '#f97316', // Orange - giving feedback
    mp_360_feedback_as_reviewee: '#ec4899', // Pink - receiving feedback

    // Cycles widgets
    cycles_overview_summary: '#6366f1',   // Indigo
    cycles_active_count: '#22c55e',       // Green
    cycles_completion_average: '#f59e0b', // Amber
    cycles_at_risk_count: '#ef4444',      // Red
    cycles_by_type: '#8b5cf6',            // Purple
    cycles_by_completion: '#3b82f6',      // Blue
    cycles_list: '#14b8a6',              // Teal
    cycles_at_risk_list: '#ef4444',       // Red

    // Goalboard widgets
    goalboard_employee_summary: '#3b82f6',        // Blue
    goalboard_goal_status_summary: '#10b981',     // Emerald
    goalboard_goals_list: '#14b8a6',              // Teal
    goalboard_employees_without_goals: '#f97316', // Orange
    goalboard_stale_goals: '#ef4444',             // Red

    // IDP widgets
    idp_overview_summary: '#8b5cf6',   // Purple
    idp_by_status: '#a855f7',          // Purple-400
    idp_list: '#7c3aed',              // Violet
    idp_completion_rate: '#6d28d9',    // Violet-700
    idp_employees_without_idps: '#f97316', // Orange

    // PIP widgets
    pip_overview_summary: '#ef4444',   // Red
    pip_by_status: '#dc2626',          // Red-600
    pip_list: '#f87171',              // Red-400
    pip_at_risk_list: '#ef4444',       // Red
    pip_success_rate: '#22c55e',       // Green

    // Survey widgets
    survey_overview_summary: '#06b6d4', // Cyan
    survey_response_rate: '#0891b2',    // Cyan-600
    survey_by_status: '#22d3ee',       // Cyan-400
    survey_list: '#0e7490',            // Cyan-700
    survey_participation_by_department: '#14b8a6', // Teal

    // Card view widget types
    cycle_card: '#6366f1',                          // Indigo - review cycles
    at_risk_card: '#ef4444',                        // Red - at-risk items
    goal_card: '#14b8a6',                           // Teal - goals
    employee_card: '#3b82f6',                       // Blue - employees
    stale_goal_card: '#f97316',                     // Orange - stale/outdated
  };

  // Strip scoping suffix to match base template key
  const baseKey = (template.key || '')
    .replace(/_(?:global_hr|executive|manager|scoped_hr|self)$/, '');

  if (baseKey && widgetColors[baseKey]) {
    return widgetColors[baseKey];
  }

  // Learn module widgets — match by prefix
  const learnPrefixColors: Record<string, string> = {
    'learn_overview_': '#059669',    // Green (Learn brand)
    'learn_enrollment_': '#059669',
    'learn_completion_': '#3b82f6',  // Blue
    'learn_overdue_': '#ef4444',     // Red
    'learn_course_': '#8b5cf6',      // Purple
    'learn_license_': '#f59e0b',     // Amber
    'learn_lp_': '#0ea5e9',          // Sky
    'learn_assignment_': '#ec4899',  // Pink
    'learn_avg_': '#14b8a6',         // Teal
    'learn_cust_': '#f59e0b',        // Amber (customer)
    'learn_marketplace_': '#f97316', // Orange
    'learn_trainer_': '#7c3aed',     // Violet
    'learn_ext_': '#7c3aed',         // Violet
    'learn_ilt_': '#6366f1',         // Indigo
    'learn_mgr_': '#3b82f6',         // Blue
    'learn_coach_': '#0e7490',       // Cyan
    'learn_uh_': '#0891b2',          // Cyan-600
    'mp_learn_': '#059669',          // Green (Learn brand)
  };
  for (const [prefix, color] of Object.entries(learnPrefixColors)) {
    if (baseKey.startsWith(prefix)) return color;
  }

  const chartColors: Record<string, string> = {
    funnel: '#8b5cf6',
    bar: '#3b82f6',
    line: '#10b981',
    gauge: '#f59e0b',
    pie: '#ec4899',
    donut: '#ec4899',
    kpi_card: '#3b82f6',
    multi_stat: '#6366f1',
    map: '#10b981',
    pip_card: '#ef4444',
    idp_card: '#8b5cf6',
    survey_card: '#06b6d4',
    card: '#6366f1',
    table: '#14b8a6',
    cycle_card: '#6366f1',
    at_risk_card: '#ef4444',
    goal_card: '#14b8a6',
    employee_card: '#3b82f6',
    stale_goal_card: '#f97316',
  };

  const chartType = (template.default_config as any)?.chart_type as string;
  if (chartType && chartColors[chartType]) {
    return chartColors[chartType];
  }
  if ((template.default_config as any)?.display_type === 'card') {
    return chartColors.card;
  }
  if ((template.default_config as any)?.page_size) {
    return chartColors.table;
  }
  return '#6b7280';
};

// Helper to get a human-friendly label and color for scoping values
const getScopingBadge = (scoping: string): { label: string; color: string; bg: string } => {
  switch (scoping) {
    case 'self':
      return { label: 'Personal', color: '#6d28d9', bg: '#ede9fe' };
    case 'scoped_hr':
      return { label: 'HR', color: '#0369a1', bg: '#e0f2fe' };
    case 'global_hr':
      return { label: 'Organization', color: '#0e7490', bg: '#cffafe' };
    case 'manager':
      return { label: 'Team', color: '#047857', bg: '#d1fae5' };
    case 'executive':
      return { label: 'Executive', color: '#b45309', bg: '#fef3c7' };
    case 'admin':
      return { label: 'Admin', color: '#7e22ce', bg: '#f3e8ff' };
    case 'learn_admin':
      return { label: 'Learn Admin', color: '#059669', bg: '#d1fae5' };
    case 'unit_head':
      return { label: 'Unit Head', color: '#0e7490', bg: '#cffafe' };
    default:
      // Format unknown values: snake_case → Title Case
      return {
        label: scoping.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        color: '#4b5563',
        bg: '#f3f4f6',
      };
  }
};

// Module and submodule chip configs for subscription gating display
const MODULE_CHIP_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  'learn':          { label: 'Learn',          color: '#059669', bg: '#d1fae5' },
  'review-cloud':   { label: 'PerformSpark',   color: '#4B45E5', bg: '#EEF2FF' },
  'comp-builder':   { label: 'Comp Builder',   color: '#b45309', bg: '#fef3c7' },
  'recognition':    { label: 'Recognition',    color: '#ec4899', bg: '#fce7f3' },
  'custom-reports': { label: 'Custom Reports', color: '#0369a1', bg: '#e0f2fe' },
};

const SUBMODULE_CHIP_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  'lms':                    { label: 'LMS',              color: '#0369a1', bg: '#e0f2fe' },
  'training-management':    { label: 'Training Mgmt',    color: '#7c3aed', bg: '#ede9fe' },
  'coaching':               { label: 'Coaching',         color: '#0e7490', bg: '#cffafe' },
  'goal-management':        { label: 'Goals',            color: '#047857', bg: '#d1fae5' },
  'performance-management': { label: 'Performance',      color: '#6366f1', bg: '#e0e7ff' },
  'cycle-management':       { label: 'Cycles',           color: '#7c3aed', bg: '#ede9fe' },
  'survey':                 { label: 'Survey',            color: '#06b6d4', bg: '#cffafe' },
  'talent-management':      { label: 'Talent',           color: '#b45309', bg: '#fef3c7' },
  'position-manager':       { label: 'Positions',        color: '#0369a1', bg: '#e0f2fe' },
  'job-architecture':       { label: 'Job Architecture', color: '#4b5563', bg: '#f3f4f6' },
  'total-rewards':          { label: 'Total Rewards',    color: '#047857', bg: '#d1fae5' },
  'mrktbldr':               { label: 'Market Data',      color: '#b45309', bg: '#fef3c7' },
  'recognition':            { label: 'Recognition',      color: '#ec4899', bg: '#fce7f3' },
};

// Helper to get group name from widget key prefix (model)
const getGroupFromKey = (key: string): string => {
  // Map key prefixes to friendly group names
  // More specific prefixes must come before generic ones (mp_learn_ before mp_)
  const prefixLabels: [string, string][] = [
    ['rb_', 'Custom Reports'],
    ['wf_', 'Workforce Analytics'],
    ['mp_learn_', 'My Learning'],
    ['mp_', 'My Portal'],
    ['learn_overview_', 'Learning Overview'],
    ['learn_enrollment_', 'Learning Overview'],
    ['learn_completion_', 'Learning Analytics'],
    ['learn_overdue_', 'Learning Analytics'],
    ['learn_course_', 'Learning Analytics'],
    ['learn_license_', 'Learning Analytics'],
    ['learn_lp_', 'Learning Analytics'],
    ['learn_assignment_', 'Learning Analytics'],
    ['learn_avg_', 'Learning Analytics'],
    ['learn_cust_', 'Customer Management'],
    ['learn_marketplace_', 'Customer Management'],
    ['learn_trainer_', 'Training Delivery'],
    ['learn_ilt_', 'Training Delivery'],
    ['learn_ext_', 'Training Delivery'],
    ['learn_mgr_', 'Team Learning'],
    ['learn_coach_', 'Coaching'],
    ['learn_uh_', 'Unit Analytics'],
    ['cycles_', 'Review Cycles'],
    ['goalboard_', 'Goal Management'],
    ['idp_', 'Development Plans'],
    ['pip_', 'Performance Improvement'],
    ['survey_', 'Surveys'],
    ['rc_', 'Review Cloud'],
  ];

  for (const [prefix, label] of prefixLabels) {
    if (key.startsWith(prefix)) {
      return label;
    }
  }

  // Fallback: try to extract prefix and format it
  const underscoreIndex = key.indexOf('_');
  if (underscoreIndex > 0) {
    const prefix = key.substring(0, underscoreIndex);
    return prefix.charAt(0).toUpperCase() + prefix.slice(1) + ' Widgets';
  }

  return 'Other Widgets';
};

const SCOPE_ORDER = ['self', 'manager', 'unit_head', 'learn_admin', 'scoped_hr', 'global_hr', 'executive', 'admin'] as const;

// Group icon mapping for quick nav
const getGroupIcon = (groupName: string): React.ReactNode => {
  const iconProps = { size: 14, weight: 'bold' as const };
  switch (groupName) {
    case 'My Portal': return <UserCircle {...iconProps} />;
    case 'Workforce Analytics': return <Users {...iconProps} />;
    case 'Goal Management': return <Target {...iconProps} />;
    case 'Review Cycles': return <ArrowsClockwise {...iconProps} />;
    case 'Development Plans': return <Star {...iconProps} />;
    case 'Performance Improvement': return <Warning {...iconProps} />;
    case 'Surveys': return <ChatCircleDots {...iconProps} />;
    case 'Learning Overview': return <GraduationCap {...iconProps} />;
    case 'Learning Analytics': return <ChartBar {...iconProps} />;
    case 'Customer Management': return <Buildings {...iconProps} />;
    case 'Training Delivery': return <Briefcase {...iconProps} />;
    case 'Team Learning': return <Users {...iconProps} />;
    case 'My Learning': return <GraduationCap {...iconProps} />;
    case 'Coaching': return <Handshake {...iconProps} />;
    case 'Unit Analytics': return <TreeStructure {...iconProps} />;
    case 'Custom Reports': return <Table {...iconProps} />;
    default: return <Cube {...iconProps} />;
  }
};

const WidgetTemplateLibrary: React.FC<WidgetTemplateLibraryProps> = ({
  isOpen,
  onClose,
  templates,
  onAddTemplate,
  onAddTemplates,
  loading = false,
}) => {
  // Subscription gating — mirror MyPortalPage so unsubscribed modules/submodules
  // never appear as tabs, chips, or widget cards in the library.
  const { user } = useAuth();
  const isSubscriptionLoading = useSubscriptionStore((s) => s.isLoading);
  const isModuleEnabled = useSubscriptionStore((s) => s.isModuleEnabled);
  const isModuleActive = useSubscriptionStore((s) => s.isModuleActive);
  const isSubmoduleEnabled = useSubscriptionStore((s) => s.isSubmoduleEnabled);
  const isCustomerUser = (user as any)?.user_category === 'customer_employee';

  const [activeModule, setActiveModule] = React.useState<string>('all');
  const [activeSubmodule, setActiveSubmodule] = React.useState<string>('all');
  const [activeScope, setActiveScope] = React.useState<string>('all');
  const [activeGroup, setActiveGroup] = React.useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const contentRef = useRef<HTMLDivElement>(null);

  const toggleSelection = useCallback((templateId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(templateId)) {
        next.delete(templateId);
      } else {
        next.add(templateId);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Reset all filters when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setActiveModule('all');
      setActiveSubmodule('all');
      setActiveScope('all');
      setActiveGroup(null);
      setSelectedIds(new Set());
    }
  }, [isOpen]);

  // Reset submodule and scope when module changes
  React.useEffect(() => {
    setActiveSubmodule('all');
    setActiveScope('all');
  }, [activeModule]);

  // Reset scope when submodule changes
  React.useEffect(() => {
    setActiveScope('all');
  }, [activeSubmodule]);

  // Scroll to section and highlight nav item
  const scrollToGroup = useCallback((groupName: string) => {
    const sectionId = `widget-group-${groupName.replace(/\s+/g, '-').toLowerCase()}`;
    const element = document.getElementById(sectionId);
    if (element && contentRef.current) {
      const container = contentRef.current;
      const offsetTop = element.offsetTop - container.offsetTop;
      container.scrollTo({ top: offsetTop - 8, behavior: 'smooth' });
      setActiveGroup(groupName);
    }
  }, []);

  // Track active section on scroll
  const handleContentScroll = useCallback(() => {
    if (!contentRef.current) return;
    const container = contentRef.current;
    const scrollTop = container.scrollTop;
    const sections = container.querySelectorAll('[data-group-name]');
    let current: string | null = null;

    sections.forEach((section) => {
      const el = section as HTMLElement;
      const offsetTop = el.offsetTop - container.offsetTop;
      if (scrollTop >= offsetTop - 40) {
        current = el.getAttribute('data-group-name');
      }
    });

    if (current !== activeGroup) {
      setActiveGroup(current);
    }
  }, [activeGroup]);

  // Predicate: should this template appear given the user's subscription?
  // Mirrors the MyPortalPage gating exactly (same store helpers, same bypasses).
  const isTemplateSubscribed = React.useCallback((t: WidgetTemplate): boolean => {
    if (isSubscriptionLoading) return true;
    if (isCustomerUser) return true;
    if (t.source_type === 'report_builder') return true;
    if (!t.subscription_module_id) return true;

    const moduleOk =
      isModuleEnabled(t.subscription_module_id) &&
      isModuleActive(t.subscription_module_id);
    if (!moduleOk) return false;

    if (t.subscription_submodule_id) {
      return isSubmoduleEnabled(t.subscription_module_id, t.subscription_submodule_id);
    }
    return true;
  }, [isSubscriptionLoading, isCustomerUser, isModuleEnabled, isModuleActive, isSubmoduleEnabled]);

  // Subscription gating — apply BEFORE deriving tabs/chips so unsubscribed
  // modules and submodules disappear from the UI entirely.
  const subscriptionFilteredTemplates = React.useMemo(() => {
    if (!Array.isArray(templates)) return [];
    return templates.filter(isTemplateSubscribed);
  }, [templates, isTemplateSubscribed]);

  // Check if there are any custom report templates
  const hasCustomReports = React.useMemo(() => {
    return subscriptionFilteredTemplates.some((t) => t.source_type === 'report_builder');
  }, [subscriptionFilteredTemplates]);

  // Derive available modules from subscription-filtered templates
  const availableModules = React.useMemo(() => {
    const moduleSet = new Set<string>();
    subscriptionFilteredTemplates.forEach((t) => {
      if (t.subscription_module_id) moduleSet.add(t.subscription_module_id);
    });
    // Fixed order: known modules first, custom reports last
    const moduleOrder = ['learn', 'review-cloud', 'comp-builder', 'recognition'];
    const result = moduleOrder.filter((m) => moduleSet.has(m));
    if (hasCustomReports) {
      result.push('custom-reports');
    }
    return result;
  }, [subscriptionFilteredTemplates, hasCustomReports]);

  // Filter by module
  const moduleFilteredTemplates = React.useMemo(() => {
    if (activeModule === 'all') return subscriptionFilteredTemplates;
    if (activeModule === 'none') return subscriptionFilteredTemplates.filter((t) => !t.subscription_module_id && t.source_type !== 'report_builder');
    if (activeModule === 'custom-reports') return subscriptionFilteredTemplates.filter((t) => t.source_type === 'report_builder');
    return subscriptionFilteredTemplates.filter((t) => t.subscription_module_id === activeModule && t.source_type !== 'report_builder');
  }, [subscriptionFilteredTemplates, activeModule]);

  // Derive available submodules from module-filtered templates
  const availableSubmodules = React.useMemo(() => {
    const subSet = new Set<string>();
    moduleFilteredTemplates.forEach((t) => {
      if (t.subscription_submodule_id) subSet.add(t.subscription_submodule_id);
    });
    // Return in a stable order based on SUBMODULE_CHIP_CONFIG keys
    return Object.keys(SUBMODULE_CHIP_CONFIG).filter((s) => subSet.has(s));
  }, [moduleFilteredTemplates]);

  // Filter by submodule
  const submoduleFilteredTemplates = React.useMemo(() => {
    if (activeSubmodule === 'all') return moduleFilteredTemplates;
    return moduleFilteredTemplates.filter((t) => t.subscription_submodule_id === activeSubmodule);
  }, [moduleFilteredTemplates, activeSubmodule]);

  // Derive available scopes from submodule-filtered templates
  const availableScopes = React.useMemo(() => {
    const scopeSet = new Set<string>();
    submoduleFilteredTemplates.forEach((t) => {
      if (t.scoping) scopeSet.add(t.scoping);
    });
    return SCOPE_ORDER.filter((s) => scopeSet.has(s));
  }, [submoduleFilteredTemplates]);

  // Final filtered templates (module → submodule → scope)
  const filteredTemplates = React.useMemo(() => {
    if (activeScope === 'all') return submoduleFilteredTemplates;
    return submoduleFilteredTemplates.filter((t) => t.scoping === activeScope);
  }, [submoduleFilteredTemplates, activeScope]);

  // Group filtered templates by key prefix (model)
  const groupedTemplates = React.useMemo(() => {
    const groups = new Map<string, WidgetTemplate[]>();

    filteredTemplates.forEach((template) => {
      const groupName = getGroupFromKey(template.key);

      if (!groups.has(groupName)) {
        groups.set(groupName, []);
      }
      groups.get(groupName)!.push(template);
    });

    // Sort groups by priority order
    const priorityOrder = ['My Portal', 'My Learning', 'Workforce Analytics', 'Learning Overview', 'Learning Analytics', 'Training Delivery', 'Team Learning', 'Coaching', 'Customer Management', 'Unit Analytics', 'Goal Management', 'Review Cycles', 'Development Plans', 'Performance Improvement', 'Surveys', 'Review Cloud', 'Custom Reports'];

    const sortedGroups = new Map<string, WidgetTemplate[]>();

    // Add priority groups first
    priorityOrder.forEach(groupName => {
      if (groups.has(groupName)) {
        sortedGroups.set(groupName, groups.get(groupName)!);
        groups.delete(groupName);
      }
    });

    // Add remaining groups alphabetically
    Array.from(groups.keys()).sort().forEach(groupName => {
      sortedGroups.set(groupName, groups.get(groupName)!);
    });

    return sortedGroups;
  }, [filteredTemplates]);

  if (!isOpen) return null;

  const handleAdd = (template: WidgetTemplate) => {
    // If nothing is selected yet, single-click still adds immediately (legacy behavior)
    if (selectedIds.size === 0) {
      onAddTemplate(template);
      onClose();
    } else {
      // If already in multi-select mode, toggle selection instead
      toggleSelection(template.id);
    }
  };

  const handleBatchAdd = () => {
    const selected = templates.filter(t => selectedIds.has(t.id));
    if (selected.length === 0) return;

    if (onAddTemplates) {
      onAddTemplates(selected);
    } else {
      // Fallback: add one by one
      selected.forEach(t => onAddTemplate(t));
    }
    onClose();
  };

  // Count for active filters
  const moduleCount = activeModule === 'all' ? templates.length : moduleFilteredTemplates.length;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Add Widget
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Select a widget to add to your dashboard
              {filteredTemplates.length !== subscriptionFilteredTemplates.length && (
                <span className="text-gray-400"> — {filteredTemplates.length} of {subscriptionFilteredTemplates.length} shown</span>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Filter Bar */}
        {!loading && subscriptionFilteredTemplates.length > 0 && (
          <div className="px-6 pt-4 pb-2 flex-shrink-0 space-y-3">
            {/* Tier 1: Module Tabs — segmented control style, largest touch targets */}
            {availableModules.length > 0 && (
              <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-xl w-fit">
                <button
                  onClick={() => setActiveModule('all')}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    activeModule === 'all'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  All
                  <span className="ml-1.5 text-xs opacity-60">{subscriptionFilteredTemplates.length}</span>
                </button>
                {availableModules.map((moduleId) => {
                  const chip = MODULE_CHIP_CONFIG[moduleId];
                  if (!chip) return null;
                  const isActive = activeModule === moduleId;
                  const count = subscriptionFilteredTemplates.filter((t) => t.subscription_module_id === moduleId).length;
                  return (
                    <button
                      key={moduleId}
                      onClick={() => setActiveModule(moduleId)}
                      className="px-4 py-2 rounded-lg text-sm font-semibold transition-all"
                      style={
                        isActive
                          ? { backgroundColor: chip.color, color: '#fff', boxShadow: `0 1px 3px ${chip.color}40` }
                          : { color: '#6b7280' }
                      }
                      onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = chip.color; }}
                      onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = '#6b7280'; }}
                    >
                      {chip.label}
                      <span className="ml-1.5 text-xs opacity-70">{count}</span>
                    </button>
                  );
                })}
                {subscriptionFilteredTemplates.some((t) => !t.subscription_module_id) && (
                  <button
                    onClick={() => setActiveModule('none')}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      activeModule === 'none'
                        ? 'bg-gray-700 text-white shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    General
                  </button>
                )}
              </div>
            )}

            {/* Tier 2: Submodule Pills — medium, with colored dot indicator */}
            {activeModule !== 'all' && availableSubmodules.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mr-1">Feature</span>
                <button
                  onClick={() => setActiveSubmodule('all')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                    activeSubmodule === 'all'
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  All
                </button>
                {availableSubmodules.map((subId) => {
                  const chip = SUBMODULE_CHIP_CONFIG[subId];
                  if (!chip) return null;
                  const isActive = activeSubmodule === subId;
                  return (
                    <button
                      key={subId}
                      onClick={() => setActiveSubmodule(subId)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border"
                      style={
                        isActive
                          ? { backgroundColor: chip.bg, color: chip.color, borderColor: chip.color }
                          : { backgroundColor: '#fff', color: '#4b5563', borderColor: '#e5e7eb' }
                      }
                    >
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: chip.color }}
                      />
                      {chip.label}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Tier 3: Persona/Scoping — compact tags with role icon, distinct rounded-full shape */}
            {availableScopes.length > 1 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mr-1">View as</span>
                <button
                  onClick={() => setActiveScope('all')}
                  className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-all border ${
                    activeScope === 'all'
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Users size={12} weight="bold" />
                  All
                </button>
                {availableScopes.map((scope) => {
                  const badge = getScopingBadge(scope);
                  const isActive = activeScope === scope;
                  return (
                    <button
                      key={scope}
                      onClick={() => setActiveScope(scope)}
                      className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all border"
                      style={
                        isActive
                          ? { backgroundColor: badge.bg, color: badge.color, borderColor: badge.color }
                          : { backgroundColor: '#fff', color: '#6b7280', borderColor: '#e5e7eb' }
                      }
                    >
                      <UserCircle size={12} weight={isActive ? 'fill' : 'bold'} style={{ color: badge.color }} />
                      {badge.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Quick Nav - Section Jump Bar */}
        {!loading && filteredTemplates.length > 0 && groupedTemplates.size > 1 && (
          <div className="px-6 py-2 border-b border-gray-100 flex-shrink-0">
            <div className="flex flex-wrap items-center gap-1">
              {Array.from(groupedTemplates.keys()).map((groupName) => {
                const isActive = activeGroup === groupName;
                return (
                  <button
                    key={groupName}
                    onClick={() => scrollToGroup(groupName)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      isActive
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700 border border-transparent'
                    }`}
                  >
                    {getGroupIcon(groupName)}
                    {groupName}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-auto p-6" ref={contentRef} onScroll={handleContentScroll}>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Loading widgets...</div>
            </div>
          ) : subscriptionFilteredTemplates.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">No widgets available for this page.</div>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">No widgets match the selected filters.</div>
            </div>
          ) : (
            <div className="space-y-6">
              {Array.from(groupedTemplates.entries()).map(([groupName, groupTemplates]) => (
                <div
                  key={groupName}
                  id={`widget-group-${groupName.replace(/\s+/g, '-').toLowerCase()}`}
                  data-group-name={groupName}
                >
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                    {groupName}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {groupTemplates.map(template => (
                      <WidgetCard
                        key={template.id}
                        template={template}
                        onAdd={handleAdd}
                        isSelected={selectedIds.has(template.id)}
                        onToggleSelect={toggleSelection}
                        hasSelection={selectedIds.size > 0}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Floating action bar for multi-select */}
        {selectedIds.size > 0 && (
          <div className="flex-shrink-0 border-t border-gray-200 bg-white px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">
                <span className="font-semibold text-gray-900">{selectedIds.size}</span> widget{selectedIds.size > 1 ? 's' : ''} selected
              </span>
              <button
                onClick={clearSelection}
                className="text-xs text-gray-400 hover:text-gray-600 underline transition-colors"
              >
                Clear
              </button>
            </div>
            <button
              onClick={handleBatchAdd}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm cursor-pointer"
            >
              <Plus size={16} weight="bold" />
              Add {selectedIds.size} Widget{selectedIds.size > 1 ? 's' : ''}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Widget card with checkbox, prominent icon and description
const WidgetCard: React.FC<{
  template: WidgetTemplate;
  onAdd: (t: WidgetTemplate) => void;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  hasSelection: boolean;
}> = ({ template, onAdd, isSelected, onToggleSelect, hasSelection }) => {
  const iconColor = getTemplateColor(template);

  const handleClick = () => {
    if (hasSelection) {
      // In multi-select mode, clicks toggle selection
      onToggleSelect(template.id);
    } else {
      onAdd(template);
    }
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleSelect(template.id);
  };

  return (
    <button
      onClick={handleClick}
      className={`group relative flex items-start gap-3 p-4 rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-left cursor-pointer ${
        isSelected
          ? 'bg-blue-50 border-2 border-blue-400 shadow-sm'
          : 'bg-gray-50 hover:bg-white border border-gray-200 hover:border-gray-300 hover:shadow-lg'
      }`}
    >
      {/* Checkbox */}
      <div
        onClick={handleCheckboxClick}
        className={`absolute top-3 left-3 w-5 h-5 rounded flex items-center justify-center transition-all cursor-pointer ${
          isSelected
            ? 'bg-blue-600 text-white'
            : 'border-2 border-gray-300 bg-white opacity-0 group-hover:opacity-100'
        }`}
      >
        {isSelected && <Check size={14} weight="bold" />}
      </div>

      {/* Icon */}
      <div
        className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110 ${
          isSelected ? 'ml-4' : 'group-hover:ml-4'
        }`}
        style={{
          backgroundColor: `${iconColor}15`,
          color: iconColor,
        }}
      >
        {getTemplateIcon(template, 24)}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-gray-900 leading-tight block">
          {template.title}
        </span>
        {template.description && (
          <span className="text-xs text-gray-500 mt-1 line-clamp-2 block">
            {template.description}
          </span>
        )}
        {/* Chip row: module + submodule + scoping — each tier has distinct visual */}
        <div className="flex flex-wrap items-center gap-1 mt-1.5">
          {template.source_type === 'report_builder' ? (() => {
            const chip = MODULE_CHIP_CONFIG['custom-reports'];
            return (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold leading-tight"
                style={{ color: chip.color, backgroundColor: chip.bg }}>
                {chip.label}
              </span>
            );
          })() : template.subscription_module_id && (() => {
            const chip = MODULE_CHIP_CONFIG[template.subscription_module_id];
            if (!chip) return null;
            return (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold leading-tight"
                style={{ color: chip.color, backgroundColor: chip.bg }}>
                {chip.label}
              </span>
            );
          })()}
          {template.subscription_submodule_id && (() => {
            const chip = SUBMODULE_CHIP_CONFIG[template.subscription_submodule_id];
            if (!chip) return null;
            return (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium leading-tight"
                style={{ color: chip.color, backgroundColor: chip.bg }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: chip.color }} />
                {chip.label}
              </span>
            );
          })()}
          {template.scoping && (() => {
            const badge = getScopingBadge(template.scoping);
            return (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium leading-tight border"
                style={{ color: badge.color, borderColor: `${badge.color}40`, backgroundColor: `${badge.bg}80` }}>
                <UserCircle size={10} weight="fill" style={{ color: badge.color }} />
                {badge.label}
              </span>
            );
          })()}
        </div>
      </div>

      {/* Hover add indicator (only when no selection mode) */}
      {!hasSelection && (
        <div className="absolute top-2 right-2 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <Plus size={18} weight="bold" className="text-gray-900" />
        </div>
      )}
    </button>
  );
};

export default WidgetTemplateLibrary;
